import type { CardType, PortfolioItem, PortfolioSort } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { Eye, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";

import { useApi } from "../../app/api-context";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { ariaSort } from "../../components/data-table/aria-sort";
import { Pagination } from "../../components/data-table/pagination";
import { SortableHeader } from "../../components/data-table/sortable-header";
import { PageHeader } from "../../components/page-header";
import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input, Select } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { describeError } from "../../lib/errors";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useHasPermission } from "../auth/session";
import { formatMoney } from "../search/format";
import { describeCondition, describeSet } from "./format";
import { usePortfolioChange, usePortfolioList } from "./portfolio-queries";

const HEADERS: readonly { label: string; sort?: PortfolioSort; firstDir?: "asc" | "desc" }[] = [
  { label: "Card", sort: "name", firstDir: "asc" },
  { label: "Set", sort: "set", firstDir: "asc" },
  { label: "Condition" },
  { label: "Quantity" },
  { label: "Purchase price" },
  { label: "Added", sort: "added", firstDir: "desc" },
];

/** Every card the business owns, newest first, with a quick search, a raw/graded filter and sorting. */
export function PortfolioListPage() {
  const [text, setText] = useState("");
  const [page, setPage] = useState(1);
  const [cardType, setCardType] = useState<CardType | undefined>(undefined);
  const [sort, setSort] = useState<PortfolioSort>("added");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [removing, setRemoving] = useState<PortfolioItem | null>(null);
  const q = useDebouncedValue(text.trim(), 300);
  const list = usePortfolioList({ q, page, sort, dir, ...(cardType === undefined ? {} : { cardType }) });
  const canAdd = useHasPermission("holdings.update");
  const searchId = useId();
  const typeId = useId();
  const filtered = q !== "" || cardType !== undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Portfolio"
        description="Cards the business owns, with photos and details, for our records."
        actions={
          canAdd ? (
            <Button asChild>
              <Link to="/portfolio/new">
                <Plus aria-hidden="true" />
                Add card
              </Link>
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-56 flex-1 flex-col gap-1 sm:max-w-sm">
          <label htmlFor={searchId} className="text-xs font-medium text-muted-foreground">
            Find a card
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id={searchId}
              type="search"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setPage(1);
              }}
              placeholder="Name, set or number"
              className="ps-8"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={typeId} className="text-xs font-medium text-muted-foreground">
            Type
          </label>
          <Select
            id={typeId}
            value={cardType ?? "all"}
            onChange={(e) => {
              setCardType(e.target.value === "all" ? undefined : (e.target.value as CardType));
              setPage(1);
            }}
            className="w-40"
          >
            <option value="all">Raw and graded</option>
            <option value="raw">Raw only</option>
            <option value="graded">Graded only</option>
          </Select>
        </div>
      </div>

      {list.isPending ? (
        <Card className="flex flex-col gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
          <span className="sr-only">Loading the portfolio…</span>
        </Card>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.total === 0 ? (
        <Card className="py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {!filtered
              ? "No cards yet. Use “Add card” to record the first one."
              : q === ""
                ? "No cards of this type."
                : `No cards match “${q}”.`}
          </p>
        </Card>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table className="min-w-[860px]">
              <caption className="sr-only">Portfolio cards</caption>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {HEADERS.map((header) => (
                    <TableHead
                      key={header.label}
                      scope="col"
                      aria-sort={header.sort === undefined ? undefined : ariaSort(header.sort === sort, dir)}
                      className="text-xs uppercase tracking-wide text-muted-foreground"
                    >
                      {header.sort === undefined ? (
                        header.label
                      ) : (
                        <SortableHeader
                          label={header.label}
                          column={header.sort}
                          sort={sort}
                          dir={dir}
                          firstDir={header.firstDir}
                          onSort={(column, nextDir) => {
                            setSort(column);
                            setDir(nextDir);
                            setPage(1);
                          }}
                        />
                      )}
                    </TableHead>
                  ))}
                  <TableHead scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.data.map((item) => (
                  <CardRow key={item.id} item={item} canRemove={canAdd} onRemove={setRemoving} />
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            busy={list.isPlaceholderData}
            onPage={setPage}
            noun="cards"
          />
        </>
      )}

      {removing !== null && <RemoveCardDialog item={removing} onDone={() => setRemoving(null)} />}
    </div>
  );
}

function CardRow({
  item,
  canRemove,
  onRemove,
}: {
  item: PortfolioItem;
  canRemove: boolean;
  onRemove: (item: PortfolioItem) => void;
}) {
  const api = useApi();
  const [photo] = item.photos;
  const set = describeSet(item);
  return (
    <TableRow className="align-top text-sm">
      <TableCell>
        <div className="flex gap-3">
          {photo === undefined ? (
            <div className="h-14 w-10 shrink-0 rounded bg-muted" aria-hidden="true" />
          ) : (
            <img
              src={api.portfolioPhotoUrl(item.id, photo.id)}
              alt=""
              loading="lazy"
              className="h-14 w-10 shrink-0 rounded object-cover"
            />
          )}
          <div>
            <Link
              to="/portfolio/$itemId"
              params={{ itemId: item.id }}
              className="font-medium text-foreground hover:underline"
            >
              {item.name}
            </Link>
            <p className="text-xs text-muted-foreground">Added by {item.addedBy.name}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-normal text-muted-foreground">{set === "" ? "—" : set}</TableCell>
      <TableCell>{describeCondition(item)}</TableCell>
      <TableCell>{item.quantity}</TableCell>
      <TableCell>
        {item.costHidden ? (
          <span className="text-muted-foreground">Hidden</span>
        ) : item.purchasePrice === null || item.purchaseCurrency === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          formatMoney({ amount: item.purchasePrice, currency: item.purchaseCurrency })
        )}
        {item.purchaseDate !== null && (
          <p className="text-xs text-muted-foreground">Bought {item.purchaseDate}</p>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">{item.addedAt.slice(0, 10)}</TableCell>
      <TableCell className="text-end">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for ${item.name}`}>
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link to="/portfolio/$itemId" params={{ itemId: item.id }}>
                <Eye aria-hidden="true" />
                Open
              </Link>
            </DropdownMenuItem>
            {canRemove && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => onRemove(item)}>
                  <Trash2 aria-hidden="true" />
                  Remove…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

function RemoveCardDialog({ item, onDone }: { item: PortfolioItem; onDone: () => void }) {
  const api = useApi();
  const remove = usePortfolioChange(() => api.removePortfolioItem(item.id));
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !remove.isPending) onDone();
      }}
      title={`Remove ${item.name}?`}
      description="It leaves the portfolio list. Its record stays in the activity log."
      confirmText="Remove"
      destructive
      busy={remove.isPending}
      onConfirm={() =>
        remove.mutate(undefined, {
          onSuccess: () => {
            toast.success(`${item.name} removed from the portfolio.`);
            onDone();
          },
          onError: (error) => {
            toast.error(describeError(error).message);
            onDone();
          },
        })
      }
    />
  );
}
